import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Radio, Loader2 } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";

const BG =
  "https://images.unsplash.com/photo-1586449480537-3a22cf98b04c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwZmliZXIlMjBvcHRpYyUyMG5ldHdvcmslMjBub2RlcyUyMG1hcHxlbnwwfHx8fDE3OTA5NTAxNDd8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/");
  };

  return (
    <div className="min-h-dvh grid lg:grid-cols-2 bg-background">
      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle />
      </div>
      <div className="relative hidden lg:block overflow-hidden border-r border-border">
        <img src={BG} alt="fiber network" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12">
          <div className="font-heading font-black text-4xl tracking-tight text-white mb-3 drop-shadow-md">
            POP NETWORK<br />MONITORING
          </div>
          <p className="text-slate-200 text-sm max-w-md font-mono">
            Real-time MikroTik &amp; backbone topology intelligence for ISP / FTTH operations centers.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-8 pt-[max(2rem,env(safe-area-inset-top))] pb-[max(2rem,env(safe-area-inset-bottom))]">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
              <Radio className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <div className="font-heading font-black text-lg tracking-tight">POP MONITOR</div>
              <div className="text-[10px] text-muted-foreground font-mono">NOC CONSOLE ACCESS</div>
            </div>
          </div>

          <h1 className="font-heading font-black text-2xl mb-1">Sign in</h1>
          <p className="text-muted-foreground text-sm mb-6">Authenticate to access the console.</p>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="mt-1 font-mono text-sm"
                required
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 font-mono text-sm"
                required
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full rounded-md font-heading font-bold"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "ACCESS CONSOLE"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
